'use client';

import React, { Suspense } from 'react';
import { ChevronLeft, SlidersHorizontal } from 'lucide-react';

import Container from '@/components/layouts/container';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSearchParamsContext } from '@/contexts/search-params-context';
import { getFilterCount } from '@/lib/utils';

import { FiltersContent } from './filters/filters-content';
import { MobileSearchForm } from './mobile-search-form';

interface MobileSearchMenuProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
}

export const MobileSearchMenu: React.FC<MobileSearchMenuProps> = ({
  isOpen,
  setIsOpen,
  isFilterOpen,
  setIsFilterOpen,
}) => {
  const { filters, clearFilters, applyFilters } = useSearchParamsContext();
  const filterCount = getFilterCount(filters);

  const handleApplyFilters = () => {
    applyFilters();
    setIsFilterOpen(false);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    clearFilters();
    setIsFilterOpen(false);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50 md:hidden'
      role='dialog'
      aria-modal='true'
      aria-label='Mobile search modal'
    >
      <div
        className='absolute inset-0 bg-[#414141]/80'
        onClick={() => setIsOpen(false)}
        aria-hidden='true'
      />

      <Container className='relative z-10 flex h-dashboard-navbar-height-mobile items-center gap-2 bg-white shadow-sm'>
        <button
          className='py-2 pr-1'
          aria-label='Close search modal'
          onClick={() => setIsOpen(false)}
        >
          <ChevronLeft className='size-6 text-brand-green' />
        </button>
        <Suspense fallback={<Skeleton className='h-10 w-full flex-1' />}>
          <MobileSearchForm
            onSearch={() => {
              setIsOpen(false);
              setIsFilterOpen(false);
            }}
          />
        </Suspense>

        <button
          className='relative flex items-center gap-2 rounded-md p-2 text-sm text-brand-green hover:bg-brand-soft/25 focus-visible:ring-brand-green focus-visible:ring-offset-0'
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          aria-expanded={isFilterOpen}
          aria-controls='mobile-search-filter-section'
          type='button'
        >
          <div className='flex items-center gap-2'>
            <SlidersHorizontal className='size-4' />
            Filter
          </div>
          {filterCount > 0 && (
            <Badge
              variant='filter-count'
              className='absolute -right-1 -top-1 bg-white'
            >
              {filterCount}
            </Badge>
          )}
        </button>
      </Container>

      {isFilterOpen && (
        <Container className='relative z-10 max-h-[calc(100vh-var(--dashboard-navbar-height-mobile))] animate-fade-in overflow-y-auto rounded-b-lg border-t border-gray-200 bg-white py-4 duration-75'>
          <FiltersContent variant='desktop' />
          <div className='flex flex-col gap-2 2xs:flex-row'>
            <Button
              onClick={handleClearFilters}
              variant='secondary'
              className='flex-1'
            >
              Reset All
            </Button>
            <Button onClick={handleApplyFilters} className='flex-1'>
              Apply Filters
            </Button>
          </div>
        </Container>
      )}
    </div>
  );
};
